import React from "react";
import Head from "../Components/Layout/Head";
import Footer from "../Components/Layout/Footer";
import {
  Search,
  Lightbulb,
  Settings,
  TrendingUp,
  Sparkles,
  XCircle,
  CheckCircle,
  Bot,
  Workflow,
  FileSearch,
  DatabaseZap,
  BrainCircuit,
  Building2,
  BriefcaseBusiness,
  Hospital,
  GraduationCap,
  Landmark,
  ArrowRight,
} from "lucide-react";
import { useState, useEffect, useContext } from "react";
import ApiPath from "../Components/Common/Apiurl";
import { CountryContext } from "../CountryContext";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import FooterNew from "../Components/Layout/FooterNew";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import getHelmetContent from "../Components/Common/HelmetData";
import AOS from "aos";
import "aos/dist/aos.css";

const LandingPage = () => {

  const country = useContext(CountryContext);
  const navigate = useNavigate();
  const websiteURL = window.location.origin;

  const [formVisible, setFormVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const [selectedCountry, setSelectedCountry] = useState("India");

  const countryOptions = [
    "Australia",
    "Germany",
    "India",
    "New Zealand",
    "United States",
    "Other",
  ];

  const [phone, setPhone] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    message: "",
    firmType: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    firmType: '',
  });


  useEffect(() => {
    if (countryOptions.includes(country)) {
      setSelectedCountry(country);
    } else {
      setSelectedCountry("India");
    }
  }, [country]);

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  const services = [
    {
      title: "AI Automation",
      icon: <Bot size={26} />,
      desc: "Automate repetitive business tasks using AI systems that reduce manual work and improve speed.",
    },
    {
      title: "Workflow Automation",
      icon: <Workflow size={26} />,
      desc: "Connect approvals, documents, teams, dashboards, and systems into smooth automated workflows.",
    },
    {
      title: "Document Intelligence",
      icon: <FileSearch size={26} />,
      desc: "Extract, search, summarize, and understand information from PDFs, invoices, contracts, and business documents.",
    },
    {
      title: "Data Automation",
      icon: <DatabaseZap size={26} />,
      desc: "Collect, process, validate, and analyze business data to generate faster operational insights.",
    },
  ];

  const industries = [
    {
      title: "Legal",
      icon: <Landmark size={22} />,
    },
    {
      title: "Manufacturing",
      icon: <Building2 size={22} />,
    },
    {
      title: "Healthcare",
      icon: <Hospital size={22} />,
    },
    {
      title: "EdTech",
      icon: <GraduationCap size={22} />,
    },
    {
      title: "Enterprise",
      icon: <BriefcaseBusiness size={22} />,
    },
  ];
  const steps = [
    {
      icon: <Search size={28} />,
      title: "Business Analysis",
      desc: "We analyze your workflows and identify areas where automation can create measurable business impact.",
    },
    {
      icon: <Lightbulb size={28} />,
      title: "Identify Automation Opportunities",
      desc: "We map repetitive processes and design AI-driven solutions to improve efficiency and reduce costs.",
    },
    {
      icon: <Settings size={28} />,
      title: "Build & Implement AI Solution",
      desc: "We develop and integrate custom AI systems tailored to your business operations and tools.",
    },
    {
      icon: <TrendingUp size={28} />,
      title: "Monitor & Optimize",
      desc: "We continuously improve performance and scale automation for long-term growth and ROI.",
    },
  ];
  const comparisonData = [
    {
      before: "Manual Work",
      after: "Automated Workflows",
    },
    {
      before: "Slow Response",
      after: "Instant Action",
    },
    {
      before: "Frequent Errors",
      after: "High Accuracy",
    },
    {
      before: "Data Overload",
      after: "Actionable Insights",
    },
    {
      before: "High Cost",
      after: "Cost Efficiency",
    },
    {
      before: "Limited Scale",
      after: "Scalable Systems",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });

    if (name === "country") {
      setSelectedCountry(value);
    }

    // Reset phone when country dropdown changes
    if (name === "country") {
      if (value !== selectedCountry) {
        setSelectedCountry(value);
        setPhone(""); // clear phone input
        setErrors((prev) => ({
          ...prev,
          phone: "",
          country: "",
        }));
      }
    }

  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {
      name: "",
      email: "",
      phone: "",
      country: "",
      firmType: "",
    };

    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!phone) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!isValidPhoneNumber(phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!formData.firmType) {
      newErrors.firmType = "Please select firm type";
      isValid = false;
    }

    if (!selectedCountry) {
      newErrors.country = "Please select country";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) return;

    const payload = {
      name: formData.name,
      email: formData.email.trim(),
      phone: phone,
      lookingFor: formData.firmType,
      queryPageIndex: "AI_AUTOMATION_PAGE",
      message:
        (formData.message || "") +
        ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
    };

    try {
      setLoading(true);

      const response = await fetch(ApiPath + "/api/enquiryController/tqAiEnquiry", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      // const response = await fetch(ApiPath + "/tqenquiry", {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //   },
      //   body: JSON.stringify(payload),
      // });

      if (response.ok) {
        setFormData({
          name: "",
          email: "",
          firmType: "",
          message: "",
        });

        if (window.fbq) {
          window.fbq("track", "Lead");
        }

        setPhone("");
        setFormVisible(false);

        setTimeout(() => {
          setFormVisible(true);
          setLoading(false);
        }, 5000);
      } else {
        setFormError("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setFormError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const getCountryCode = (country) => {
    const map = {
      India: "IN",
      "United States": "US",
      Australia: "AU",
      Germany: "DE",
      Japan: "JP",
      Singapore: "SG",
    };
    return map[country] || "US";
  };

  const navigateToWebinar = () => {
    if (window.location.pathname !== "/registerWebinar") {
      navigate("/registerWebinar");
    }
  };

  const pageTitle = "AI Automation Solutions for Business Efficiency | Transform with AI";
  const metaTitle = "AI Automation Solutions for Business Efficiency | Transform with AI";
  const helmetContent = getHelmetContent();


  return (
    <>

      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={metaTitle} />
        {/* Remaining meta tags */}
        {helmetContent.meta.map((metaTag, index) => (
          <meta key={index} name={metaTag.name} content={metaTag.content} />
        ))}

        {/* Script for Facebook */}
        {helmetContent.script}

        {/* Updated Google Tag Manager */}
        <script>
          {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-KTNBVH9V');
          `}
        </script>

        {/* Google Analytics Scripts */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=UA-143016865-1"></script>
        <script>
          {`
          window.dataLayer = window.dataLayer || []; 
          function gtag() { 
            dataLayer.push(arguments); 
          } 
          gtag("js", new Date()); 
          gtag("config", "UA-143016865-1");
          `}
        </script>

        <script async src="https://www.googletagmanager.com/gtag/js?id=G-BSFPPN5E9D"></script>
        <script>
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag() { 
            dataLayer.push(arguments); 
          }
          gtag('js', new Date());
          gtag('config', 'G-BSFPPN5E9D');
          `}
        </script>

        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-574047969"></script>
        <script>
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag() { 
            dataLayer.push(arguments); 
          }
          gtag('js', new Date());
          gtag('config', 'AW-574047969');
          `}
        </script>

        {/* Google Tag Manager noscript fallback */}
        <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTNBVH9V" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}</noscript>

        {/* Facebook Pixel Code */}

        <script>{`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1317328810324214');
        fbq('track', 'PageView');
        `}
        </script>
        <noscript>{`<img height="1" width="1" style="display:none"
          src="https://www.facebook.com/tr?id=1317328810324214&ev=PageView&noscript=1"
        />`}</noscript>

      </Helmet>

      <Head />
      <div className="overflow-hidden">

        <div className="bg-white text-gray-800">

          {/* HERO */}
          <section
            className="relative py-12 sm:py-16 lg:py-12 overflow-hidden bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('/assets/images/6114100.jpg')",
            }}
          >
            {/* Background overlays */}
            <div className="absolute inset-0 bg-black/6"></div>

            {/* Soft gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#12002a]/30 via-transparent to-[#12002a]/25"></div>

            {/* Extra soft glow */}
            <div
              data-aos="zoom-in"
              data-aos-delay="100"
              data-aos-duration="1200"
              className="absolute top-0 left-0 w-[420px] h-[420px] bg-blue-500/10 rounded-full blur-3xl"
            ></div>

            <div
              data-aos="zoom-in"
              data-aos-delay="250"
              data-aos-duration="1200"
              className="absolute bottom-0 right-0 w-[420px] h-[420px] bg-purple-500/15 rounded-full blur-3xl"
            ></div>

            {/* Content */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-0 md:px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 flex flex-col lg:flex-row items-center gap-10">

              {/* LEFT CONTENT */}
              {/* LEFT CONTENT */}
              <div className="w-full lg:w-2/3 px-4 sm:px-6 text-center lg:text-left">
                <span
                  data-aos="fade-up"
                  data-aos-delay="100"
                  className="inline-block bg-white/15 text-white border border-white/20 px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm"
                >
                  AI Automation Solutions
                </span>

                <h1
                  data-aos="fade-up"
                  data-aos-delay="200"
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mt-4 leading-tight text-white max-w-3xl"
                >
                  Eliminate Manual Work with AI-Powered Automation
                </h1>

                <p
                  data-aos="fade-up"
                  data-aos-delay="300"
                  className="mt-4 text-base sm:text-lg text-white/90 max-w-xl mx-auto lg:mx-0"
                >
                  Build intelligent workflows, automate processes, and scale your business with AI solutions designed for real impact.
                </p>

                <div
                  data-aos="fade-up"
                  data-aos-delay="400"
                  className="mt-6 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                >
                  {/* Button if needed later */}
                </div>
              </div>

              {/* RIGHT SIDE FORM */}
              <div
                data-aos="fade-left"
                data-aos-delay="300"
                data-aos-duration="1000"
                className="w-full lg:w-1/3 flex justify-center lg:justify-end mt-10 lg:mt-0 px-4 sm:px-6"
              >
                <div className="w-full max-w-lg bg-white/95 backdrop-blur-md shadow-2xl md:rounded-2xl p-5 sm:p-6 md:p-8 border border-white/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_25px_80px_rgba(0,0,0,0.35)]">

                  {/* Success Message */}
                  {!formVisible && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-5 py-5 rounded-xl mb-4 shadow-sm">
                      <div className="flex flex-col items-center text-center">
                        <CheckCircle
                          size={48}
                          className="text-blue-600 mb-3"
                          strokeWidth={2.2}
                        />

                        <h2 className="text-xl font-semibold mb-2">
                          Thank You!
                        </h2>

                        <p className="text-sm sm:text-base text-blue-700">
                          Thank you for contacting us. We'll get back to you soon.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {formError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-5 rounded-xl mb-4 shadow-sm">
                      <div className="flex flex-col items-center text-center">
                        <XCircle
                          size={44}
                          className="text-red-500 mb-3"
                          strokeWidth={2.2}
                        />

                        <h2 className="text-lg font-semibold mb-2">
                          Submission Failed
                        </h2>

                        <p className="text-sm sm:text-base">
                          {formError}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Form */}
                  {formVisible && (
                    <>
                      <h3 className="text-xl font-semibold text-center mb-6 text-gray-900">
                        Book Appointment
                      </h3>

                      <form onSubmit={handleSubmit} className="space-y-4">

                        {/* Name */}
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="Full Name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border 
                    ${errors.name ? "border-red-500" : "border-gray-300"} 
                    focus:ring-2 focus:ring-blue-500 outline-none`}
                          />
                          {errors.name && (
                            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                          )}
                        </div>

                        {/* Phone */}
                        <div className="w-full">
                          <div
                            className={`rounded-lg border px-3 py-2 bg-white ${errors.phone ? "border-red-500" : "border-gray-300"
                              }`}
                          >
                            <PhoneInput
                              key={selectedCountry}
                              countries={["IN", "US", "AU", "DE", "JP", "SG"]}
                              defaultCountry={getCountryCode(selectedCountry)}
                              international
                              value={phone}
                              onChange={(value) => {
                                setPhone(value || "");
                                setErrors({
                                  ...errors,
                                  phone: "",
                                });
                              }}
                              placeholder="Enter phone number"
                            />

                            {errors.phone && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.phone}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Email */}
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email ID"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border 
                    ${errors.email ? "border-red-500" : "border-gray-300"} 
                    focus:ring-2 focus:ring-blue-500 outline-none`}
                          />
                          {errors.email && (
                            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                          )}
                        </div>

                        {/* Country */}
                        <div>
                          <select
                            id="country"
                            name="country"
                            className={`w-full px-4 py-2 rounded-lg border 
                    ${errors.country ? "border-red-500" : "border-gray-300"} 
                    focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-700`}
                            onChange={handleChange}
                            value={selectedCountry}
                            required
                          >
                            {countryOptions.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Firm Type */}
                        <div>
                          <select
                            name="firmType"
                            value={formData.firmType}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 rounded-lg border 
                    ${errors.firmType ? "border-red-500" : "border-gray-300"} 
                    focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 bg-white`}
                          >
                            <option value="">Select Firm Type</option>
                            <option value="Law and Legal">Law and Legal</option>
                            <option value="Manufacturing">Manufacturing</option>
                            <option value="Healthcare">Healthcare</option>
                            <option value="CA Firm">CA Firm</option>
                            <option value="Real Estate">Real Estate</option>
                            <option value="EdTech">EdTech</option>
                            <option value="Enterprise">Enterprise</option>
                            <option value="Other Firm">Other Firm</option>
                          </select>

                          {errors.firmType && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.firmType}
                            </p>
                          )}
                        </div>

                        {/* Submit */}
                        <button
                          type="submit"
                          disabled={loading}
                          className={`group relative w-full overflow-hidden rounded-xl px-6 py-3.5 
    text-sm font-semibold text-white shadow-lg transition-all duration-300
    ${loading
                              ? "cursor-not-allowed bg-gray-400 shadow-none"
                              : "bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:-translate-y-0.5 hover:shadow-blue-500/30 active:translate-y-0"
                            }`}
                        >
                          {/* soft shine effect */}
                          {!loading && (
                            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"></span>
                          )}

                          <span className="relative z-10 flex items-center justify-center gap-2">
                            {loading ? (
                              <>
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
                                Submitting...
                              </>
                            ) : (
                              <>
                                Submit Enquiry
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2.2}
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                              </>
                            )}
                          </span>
                        </button>

                      </form>
                    </>
                  )}

                </div>
              </div>

            </div>
          </section>

          {/* TRUST METRICS */}
          <section className="relative bg-white py-10 sm:py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div
                data-aos="fade-up"
                className="grid grid-cols-1 gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-[0_20px_70px_rgba(15,23,42,0.08)] sm:grid-cols-3"
              >
                {[
                  {
                    value: "40%",
                    label: "Reduction in Manual Work",
                    desc: "Through AI-powered task and document automation",
                  },
                  {
                    value: "3x",
                    label: "Faster Processing",
                    desc: "With automated workflows and intelligent assistance",
                  },
                  {
                    value: "24/7",
                    label: "AI-Enabled Support",
                    desc: "For queries, documents, search, and business operations",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={index * 120}
                    className="group rounded-3xl bg-slate-50 p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:bg-blue-50/70 hover:shadow-md"
                  >
                    <h3 className="text-3xl font-bold text-slate-950">
                      {item.value}
                    </h3>

                    <p className="mt-2 text-sm font-semibold text-slate-800">
                      {item.label}
                    </p>

                    <p className="mt-2 text-xs leading-6 text-slate-500">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* AI SOLUTIONS */}
          <section className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-24">
            <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
            <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <div
                  data-aos="fade-up"
                  className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm"
                >
                  <BrainCircuit size={16} />
                  AI Solutions
                </div>

                <h2
                  data-aos="fade-up"
                  data-aos-delay="100"
                  className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
                >
                  AI solutions designed for real business operations
                </h2>

                <p
                  data-aos="fade-up"
                  data-aos-delay="200"
                  className="mt-4 text-base leading-7 text-slate-600"
                >
                  We help businesses reduce manual work, automate document-heavy processes,
                  improve response speed, and build intelligent workflows that create measurable impact.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {services.map((item, i) => (
                  <div
                    key={i}
                    data-aos="fade-up"
                    data-aos-delay={i * 120}
                    className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-blue-100 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                  >
                    <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                      {item.icon}
                    </div>

                    <h3 className="mt-5 text-lg font-bold text-slate-950">
                      {item.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* BUSINESS TRANSFORMATION */}
          <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-24">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>

            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
              <div data-aos="fade-right">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
                  <TrendingUp size={16} />
                  Business Transformation
                </div>

                <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                  Move from slow manual work to intelligent automation
                </h2>

                <p className="mt-5 text-base leading-8 text-slate-600">
                  AI automation helps teams reduce repetitive effort, respond faster,
                  avoid manual errors, and convert large volumes of operational data into useful insights.
                </p>

                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {comparisonData.slice(0, 4).map((item, index) => (
                    <div
                      key={index}
                      data-aos="fade-up"
                      data-aos-delay={index * 90}
                      className="group rounded-2xl border border-slate-200 bg-slate-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:bg-white hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-red-500">
                        <XCircle size={17} />
                        {item.before}
                      </div>

                      <div className="my-3 h-px bg-slate-200"></div>

                      <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                        <CheckCircle size={17} />
                        {item.after}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div data-aos="fade-left">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 blur-xl"></div>

                  <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)]">
                    <div className="bg-slate-50 p-4 sm:p-6">
                      <img
                        src="/assets/images/aipoweredsolutions.jpeg"
                        alt="AI powered business automation"
                        className="mx-auto w-full max-h-[420px] object-contain"
                      />
                    </div>

                    <div className="border-t border-slate-100 bg-white p-6">
                      <p className="text-lg font-bold text-slate-950">
                        Intelligent workflows for faster, cleaner, and more accurate operations.
                      </p>

                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        From document processing to AI agents, we build systems that support daily business execution.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* HOW WE WORK */}
          <section className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-24">
            <div className="absolute -left-24 bottom-16 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
            <div className="absolute -right-24 top-16 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <div
                  data-aos="fade-up"
                  className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm"
                >
                  <Settings size={16} />
                  How We Work
                </div>

                <h2
                  data-aos="fade-up"
                  data-aos-delay="100"
                  className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
                >
                  We start with your process, then design the AI solution
                </h2>

                <p
                  data-aos="fade-up"
                  data-aos-delay="200"
                  className="mt-4 text-base leading-7 text-slate-600"
                >
                  Our approach is simple: understand your workflow, identify automation opportunities,
                  build the solution, and improve it continuously.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    data-aos="fade-up"
                    data-aos-delay={index * 120}
                    className="group relative rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_20px_60px_rgba(15,23,42,0.10)]"
                  >
                    <div className="mb-5 flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                        {step.icon}
                      </div>

                      <span className="text-sm font-bold text-slate-300">
                        0{index + 1}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-slate-950">
                      {step.title}
                    </h3>

                    <p className="mt-3 text-sm leading-7 text-slate-600">
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* INDUSTRIES */}
          <section className="relative bg-white py-16 sm:py-20 lg:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-3xl text-center">
                <div
                  data-aos="fade-up"
                  className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700"
                >
                  <Building2 size={16} />
                  Industries We Serve
                </div>

                <h2
                  data-aos="fade-up"
                  data-aos-delay="100"
                  className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
                >
                  AI automation for business-first industries
                </h2>

                <p
                  data-aos="fade-up"
                  data-aos-delay="200"
                  className="mt-4 text-base leading-7 text-slate-600"
                >
                  We build practical AI solutions for teams that handle documents, approvals,
                  customer queries, operations, and business data every day.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {industries.map((item, i) => (
                  <div
                    key={i}
                    data-aos="fade-up"
                    data-aos-delay={i * 90}
                    className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 font-semibold text-slate-700 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:bg-blue-50/50 hover:text-blue-700 hover:shadow-lg"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                      {item.icon}
                    </div>

                    <span>{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="relative overflow-hidden bg-slate-50 pb-16 sm:pb-20 lg:pb-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div
                data-aos="fade-up"
                className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_24px_80px_rgba(15,23,42,0.10)] sm:p-12"
              >
                <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400"></div>

                <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-100/80 blur-3xl"></div>
                <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-indigo-100/80 blur-3xl"></div>

                <div className="relative z-10 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
                  <div>
                    <div
                      data-aos="fade-up"
                      data-aos-delay="100"
                      className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700"
                    >
                      <Sparkles size={16} />
                      Let’s Build
                    </div>

                    <h2
                      data-aos="fade-up"
                      data-aos-delay="200"
                      className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl"
                    >
                      Ready to automate your business with AI?
                    </h2>

                    <p
                      data-aos="fade-up"
                      data-aos-delay="300"
                      className="mt-4 text-base leading-8 text-slate-600"
                    >
                      Talk to our AI experts and discover where automation can reduce manual work,
                      improve speed, and create measurable business impact.
                    </p>
                  </div>

                  <div
                    data-aos="fade-left"
                    data-aos-delay="350"
                    className="flex justify-start lg:justify-end"
                  >
                    <a
                      href="#"
                      className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-[0_18px_40px_rgba(37,99,235,0.25)]"
                    >
                      Start a Conversation
                      <ArrowRight
                        size={16}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div >

        <FooterNew />

      </div >

      <style>{`
  @keyframes heroBackgroundMove {
    0% {
      background-position: center center;
    }
    50% {
      background-position: center top;
    }
    100% {
      background-position: center center;
    }
  }

  @keyframes heroGlowMove {
    0%, 100% {
      transform: translate(0px, 0px) scale(1);
      opacity: 0.35;
    }
    50% {
      transform: translate(24px, 18px) scale(1.08);
      opacity: 0.55;
    }
  }

  .hero-bg-animate {
    animation: heroBackgroundMove 18s ease-in-out infinite;
  }

  .hero-glow-animate {
    animation: heroGlowMove 7s ease-in-out infinite;
  }
`}</style>
    </>
  );
};

export default LandingPage;